/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './BusinessCreditRiskHero.module.scss';

import { BusinessCreditRiskDecisionCard } from './BusinessCreditRiskDecisionCard/BusinessCreditRiskDecisionCard';

type Translate = (key: string) => string;

const getFigures = (t: Translate) => [
  { target: 600, suffix: 'M+', label: t('Company profiles') },
  { target: 400, suffix: '+', label: t('Government registries') },
  { target: 200, suffix: '+', label: t('Countries and territories') },
  { target: 150, suffix: '+', label: t('Parameters behind each score') },
];

const FigureValue = ({
  target,
  suffix,
  active,
  delay,
}: {
  target: number;
  suffix: string;
  active: boolean;
  delay: number;
}) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let raf = 0;
    const start = performance.now() + delay;

    const tick = (now: number) => {
      const progress = Math.min(Math.max((now - start) / 900, 0), 1);
      setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [active, target, delay]);

  return (
    <b>
      {value}
      {suffix}
    </b>
  );
};

export const BusinessCreditRiskHero = () => {
  const { t } = useInt();
  const figures = getFigures(t);
  const figuresRef = useRef<HTMLDivElement>(null);
  const figuresIn = useIntersection(figuresRef, { threshold: 0.2 });

  return (
    <Section className={styles.root}>
      <div className={styles.texture} />
      <div className={styles.glowBlue} />
      <div className={styles.glowIndigo} />

      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            theme="dark"
            title={t('Business credit risk, built on the filed record')}
            description={t(
              'Scores and recommended limits for 600M+ companies, issued with the filings they were calculated from: accounts, payment behaviour, judgments, registered security, ownership.',
            )}
          />

          <div className={styles.actions}>
            <Button type="white" size="xxl" rounded="full" href="/free-trial" suffixIcon="arrow-forward">
              {t('Credit check my company')}
            </Button>
            <Button type="white-outline" size="xxl" rounded="full" href="/book-a-demo">
              {t('Screen my suppliers')}
            </Button>
          </div>
        </div>

        <BusinessCreditRiskDecisionCard />
      </div>

      <div className={styles.figuresWrap}>
        <span className={styles.figuresLabel}>{t('Coverage at a glance')}</span>

        <div className={styles.figures} ref={figuresRef}>
          {figures.map((figure, i) => (
            <div
              className={cn(styles.figure, figuresIn && styles.figureIn)}
              key={figure.label}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <i className={styles.figureAccent} />
              <FigureValue target={figure.target} suffix={figure.suffix} active={figuresIn} delay={i * 90} />
              <span>{figure.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
